import React, { useEffect, useState, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { AppContext } from '../App';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO, { OrganizationSchema, WebSiteSchema } from '../components/SEO';
import { ShoppingCart, CheckCircle, Zap } from 'lucide-react';
import { productUrl } from '../utils/slug';
import { productImageProps } from '../utils/image';
import { toast } from 'sonner';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const translations = {
  en: {
    hero: 'Discover the Story Behind Every Stamp',
    tagline: 'Collecting History, One Stamp at a Time',
    explore: 'Explore Collections',
    featured: 'Featured Products',
    trust: 'Why Choose North Cyprus Philately',
    trustItem1: 'Trusted eBay Seller with 500+ Positive Feedback',
    trustItem2: '30+ Years of Philatelic Experience',
    trustItem3: 'Worldwide Shipping with Care and Precision',
    newsletter: 'Join Our Collectors Circle',
    newsletterDesc: 'Get notified about new arrivals and exclusive offers',
    subscribe: 'Subscribe',
    registeredMail: 'Registered Mail: $4',
    freeShipping: 'Free Shipping on orders $100+',
    discount: '10% Discount on orders $100+'
  },
  tr: {
    hero: 'Her Pulun Arkasındaki Hikayeyi Keşfedin',
    tagline: 'Tarihi Toplamak, Bir Pul Bir Pul',
    explore: 'Koleksiyonları Keşfet',
    featured: 'Öne Çıkan Ürünler',
    trust: 'Neden Kuzey Kıbrıs Filatelistlik',
    trustItem1: '500+ Olumlu Geri Bildirimli Güvenilir eBay Satıcısı',
    trustItem2: '30+ Yıllık Filatelik Deneyim',
    trustItem3: 'Dünya Çapında Özenli ve Hassas Kargo',
    newsletter: 'Koleksiyonerler Grubuna Katılın',
    newsletterDesc: 'Yeni ürünler ve özel tekliflerden haberdar olun',
    subscribe: 'Abone Ol',
    registeredMail: 'Taahhütlü Posta: $4',
    freeShipping: '$100+ siparişlerde Ücretsiz Kargo',
    discount: '$100+ siparişlerde %10 İndirim'
  },
  el: {
    hero: 'Ανακαλύψτε την Ιστορία Πίσω από Κάθε Γραμματόσημο',
    tagline: 'Συλλέγοντας Ιστορία, Ένα Γραμματόσημο τη Φορά',
    explore: 'Εξερευνήστε τις Συλλογές',
    featured: 'Προτεινόμενα Προϊόντα',
    trust: 'Γιατί να Επιλέξετε τη Φιλοτελία της Βόρειας Κύπρου',
    trustItem1: 'Αξιόπιστος Πωλητής eBay με 500+ Θετικές Αξιολογήσεις',
    trustItem2: '30+ Χρόνια Φιλοτελικής Εμπειρίας',
    trustItem3: 'Παγκόσμια Αποστολή με Φροντίδα και Ακρίβεια',
    newsletter: 'Γίνετε Μέλος της Ομάδας Συλλεκτών μας',
    newsletterDesc: 'Ενημερωθείτε για νέες αφίξεις και αποκλειστικές προσφορές',
    subscribe: 'Εγγραφή',
    registeredMail: 'Συστημένη Αποστολή: $4',
    freeShipping: 'Δωρεάν Αποστολή σε παραγγελίες $100+',
    discount: 'Έκπτωση 10% σε παραγγελίες $100+'
  },
  de: {
    hero: 'Entdecken Sie die Geschichte Hinter Jeder Briefmarke',
    tagline: 'Geschichte Sammeln, Eine Briefmarke nach der Anderen',
    explore: 'Sammlungen Erkunden',
    featured: 'Ausgewählte Produkte',
    trust: 'Warum North Cyprus Philately Wählen',
    trustItem1: 'Vertrauenswürdiger eBay-Verkäufer mit 500+ Positiven Bewertungen',
    trustItem2: '30+ Jahre Philatelistische Erfahrung',
    trustItem3: 'Weltweiter Versand mit Sorgfalt und Präzision',
    newsletter: 'Treten Sie Unserem Sammlerkreis Bei',
    newsletterDesc: 'Erhalten Sie Benachrichtigungen über Neuankömmlinge und exklusive Angebote',
    subscribe: 'Abonnieren',
    registeredMail: 'Einschreiben: $4',
    freeShipping: 'Kostenloser Versand bei Bestellungen $100+',
    discount: '10% Rabatt bei Bestellungen $100+'
  }
};

const HomePage = () => {
  const navigate = useNavigate();
  const { language, fetchCart, cart, openCartDrawer } = useContext(AppContext);
  const t = translations[language];
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [showCartReminder, setShowCartReminder] = useState(false);

  useEffect(() => {
    // Show abandoned cart reminder if user has items and hasn't dismissed
    if (cart && cart.length > 0 && !sessionStorage.getItem('cartReminderDismissed')) {
      const timer = setTimeout(() => setShowCartReminder(true), 2000);
      return () => clearTimeout(timer);
    }
  }, [cart]);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterSubmitting, setNewsletterSubmitting] = useState(false);
  const [newsletterSuccess, setNewsletterSuccess] = useState(false);

  useEffect(() => {
    fetchFeaturedProducts();
    fetchNewArrivals();
  }, []);

  const fetchFeaturedProducts = async () => {
    try {
      const response = await axios.get(`${API}/products?featured=true&limit=8`);
      // Handle both paginated and non-paginated responses
      const productsData = response.data.products || response.data;
      // Filter out deleted, archived, and out-of-stock products
      const availableProducts = productsData.filter(p => !p.deleted && !p.archived && p.stock > 0);
      setFeaturedProducts(availableProducts.slice(0, 8)); // Show 8 products for 4x2 grid
    } catch (error) {
      console.error('Error fetching featured products:', error);
    }
  };

  const fetchNewArrivals = async () => {
    try {
      const response = await axios.get(`${API}/products?sort=created_at&dir=desc&limit=4`);
      const productsData = response.data.products || response.data;
      const available = productsData.filter(p => !p.deleted && !p.archived && p.stock > 0);
      setNewArrivals(available.slice(0, 4));
    } catch (error) {
      console.error('Error fetching new arrivals:', error);
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail || !newsletterEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    setNewsletterSubmitting(true);
    try {
      await axios.post(`${API}/collectors/newsletter`, { email: newsletterEmail });
      setNewsletterSuccess(true);
      setNewsletterEmail('');
      toast.success('Welcome to our Collectors Circle!');
    } catch (error) {
      if (error.response?.status === 409) {
        toast.info('You are already subscribed!');
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } finally {
      setNewsletterSubmitting(false);
    }
  };

  return (
    <div className="home-page">
      <SEO
        title="North Cyprus Philately – Rare Stamps, Year Sets & First Day Covers from TRNC"
        description="Discover rare stamps, year sets, and first day covers from North Cyprus (TRNC). Your trusted source for collectible philately items with 30+ years experience."
        keywords="North Cyprus stamps, TRNC philately, rare stamps, first day covers, year sets, stamp collecting, Turkish Cyprus stamps, KKTC stamps, collectible stamps for sale"
        url="https://www.northcyprusphilately.com"
        lang={language}
      />
      <OrganizationSchema />
      <WebSiteSchema />
      <Header />

      {/* Abandoned Cart Reminder */}
      {showCartReminder && (
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white py-3">
          <div className="max-w-7xl mx-auto px-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-xl">🛒</span>
              <span className="font-semibold text-sm md:text-base">
                {language === 'tr'
                  ? `Sepetinizde ${cart.length} ürün var! Siparişinizi tamamlayın.`
                  : `You have ${cart.length} item${cart.length > 1 ? 's' : ''} in your cart! Complete your order.`}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => navigate('/cart')} className="bg-white text-orange-600 font-bold text-sm px-4 py-1.5 rounded-lg hover:bg-orange-50 transition">
                {language === 'tr' ? 'Sepete Git' : 'View Cart'}
              </button>
              <button onClick={() => { setShowCartReminder(false); sessionStorage.setItem('cartReminderDismissed', 'true'); }} className="text-white/70 hover:text-white text-xl leading-none">&times;</button>
            </div>
          </div>
        </div>
      )}

      {/* Shipping & Discount Banner */}
      <div className="bg-gradient-to-r from-green-600 to-blue-600 text-white py-3">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap justify-center items-center gap-4 md:gap-8 text-sm md:text-base">
            <div className="flex items-center gap-2">
              <span className="text-xl">🚢</span>
              <span className="font-semibold">{t.registeredMail}</span>
            </div>
            <div className="hidden md:block h-6 w-px bg-white opacity-50"></div>
            <div className="flex items-center gap-2">
              <span className="text-xl">✓</span>
              <span className="font-semibold">{t.freeShipping}</span>
            </div>
            <div className="hidden md:block h-6 w-px bg-white opacity-50"></div>
            <div className="flex items-center gap-2">
              <span className="text-xl">🎁</span>
              <span className="font-semibold">{t.discount}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="hero-banner">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col items-center mb-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_philately-shop/artifacts/81ter58o_77df6711-591a-4e86-9122-0f8adb5c4777.png"
              alt="North Cyprus Philately - Rare TRNC Stamps and Collectibles"
              className="h-32 w-32 mb-6"
              width="128"
              height="128"
              style={{filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.3))'}}
            />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-4" data-testid="hero-title">{t.hero}</h1>
          <p className="text-xl md:text-2xl mb-8 font-light">{t.tagline}</p>
          <button 
            className="btn-primary text-lg" 
            onClick={() => navigate('/shop')}
            data-testid="explore-btn"
          >
            {t.explore}
          </button>
        </div>
      </div>

      {featuredProducts.length > 0 && (
        <div className="py-16">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-4xl font-bold text-center mb-12" data-testid="featured-title">{t.featured}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {featuredProducts.map((product) => (
                <div 
                  key={product.id} 
                  className="product-card" 
                  onClick={() => navigate(productUrl(product))}
                  data-testid={`featured-product-${product.id}`}
                >
                  <Link to={productUrl(product)} className="relative block">
                    <img
                      {...productImageProps(
                        product.images?.[0] || '',
                        `${product.title} - ${product.condition || 'collectible'} stamp from North Cyprus`
                      )}
                      width="400"
                      height="300"
                    />
                    {product.discount_rate > 0 && (
                      <span className="absolute top-2 left-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded">
                        -{product.discount_rate}%
                      </span>
                    )}
                    {product.stock > 0 && product.stock <= 3 && (
                      <span className="absolute top-2 right-2 bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded animate-pulse">
                        Only {product.stock} left!
                      </span>
                    )}
                  </Link>
                  <div className="p-6">
                    <h3 className="text-lg font-semibold mb-2">
                      <Link to={productUrl(product)} className="hover:text-[#C8A951] transition">{product[`title_${language}`] || product.title}</Link>
                    </h3>
                    <p className="text-sm text-gray-600 mb-3">{product.condition}</p>
                    <div className="flex justify-between items-center">
                      <div>
                        {product.discounted_price && product.discounted_price < product.price ? (
                          <>
                            <span className="text-2xl font-bold" style={{color: '#B8941E'}}>${product.discounted_price}</span>
                            <span className="text-sm text-gray-400 line-through ml-2">${product.price}</span>
                          </>
                        ) : (
                          <span className="text-2xl font-bold" style={{color: '#B8941E'}}>${product.price}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <button className="btn-secondary py-1.5 px-2" onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            await axios.post(`${API}/cart/add?product_id=${product.id}&quantity=1`, {}, { withCredentials: true });
                            await fetchCart();
                            openCartDrawer();
                          } catch { toast.error('Failed'); }
                        }} title={language === 'tr' ? 'Sepete Ekle' : 'Add to Cart'}>
                          <ShoppingCart className="inline" size={18} />
                        </button>
                        <button className="btn-primary py-1.5 px-2 text-xs flex items-center gap-1" onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            await axios.post(`${API}/cart/add?product_id=${product.id}&quantity=1`, {}, { withCredentials: true });
                            await fetchCart();
                            navigate('/cart');
                          } catch { toast.error('Failed'); }
                        }} title={language === 'tr' ? 'Hemen Al' : 'Buy Now'}>
                          <Zap size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center mt-8">
              <button onClick={() => navigate('/shop')} className="btn-secondary px-8 py-3 text-lg">
                {language === 'tr' ? 'Tüm Ürünleri Gör' : language === 'de' ? 'Alle Produkte' : language === 'el' ? 'Όλα τα Προϊόντα' : 'View All Products'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Arrivals Section */}
      {newArrivals.length > 0 && (
        <div className="bg-gray-50 py-16">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between mb-12">
              <div>
                <h2 className="text-4xl font-bold">{language === 'tr' ? 'Yeni Gelenler' : language === 'de' ? 'Neuheiten' : language === 'el' ? 'Νέες Αφίξεις' : 'New Arrivals'}</h2>
                <p className="text-gray-600 mt-2">{language === 'tr' ? 'Koleksiyonumuza yeni eklenen ürünler' : 'Latest additions to our collection'}</p>
              </div>
              <button onClick={() => navigate('/shop?sort=created_at&dir=desc')} className="hidden md:block text-[#C8A951] hover:text-[#B8941E] font-semibold transition">
                {language === 'tr' ? 'Tümünü Gör →' : 'See All →'}
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {newArrivals.map((product) => (
                <Link key={product.id} to={productUrl(product)} className="product-card block">
                  <div className="relative">
                    <img
                      {...productImageProps(product.images?.[0] || '', product.title)}
                      width="400"
                      height="300"
                    />
                    <span className="absolute top-2 left-2 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded">NEW</span>
                  </div>
                  <div className="p-6">
                    <h3 className="text-lg font-semibold mb-2">{product[`title_${language}`] || product.title}</h3>
                    <span className="text-2xl font-bold" style={{color: '#B8941E'}}>
                      ${product.discounted_price && product.discounted_price < product.price ? product.discounted_price : product.price}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 py-16">
        <h2 className="text-4xl font-bold text-center mb-12" data-testid="trust-title">{t.trust}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="trust-badge" data-testid="trust-badge-0">
            <div className="text-5xl mb-4" style={{color: '#B8941E'}}>✓</div>
            <p className="text-lg">{t.trustItem1}</p>
          </div>
          <div className="trust-badge" data-testid="trust-badge-1">
            <div className="text-5xl mb-4" style={{color: '#B8941E'}}>✓</div>
            <p className="text-lg">{t.trustItem2}</p>
          </div>
          <div className="trust-badge" data-testid="trust-badge-2">
            <div className="text-5xl mb-4" style={{color: '#B8941E'}}>✓</div>
            <p className="text-lg">{t.trustItem3}</p>
          </div>
        </div>
      </div>

      {/* Social Proof Section - eBay Trust */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Trusted by Collectors Worldwide</h2>
            <p className="text-xl text-gray-600">See what our customers say about us on eBay</p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="flex items-center justify-center gap-2 mb-6">
              <img src="https://upload.wikimedia.org/wikipedia/commons/1/1b/EBay_logo.svg" alt="eBay" className="h-8" />
              <span className="text-2xl font-bold">Check Our Real Feedback on eBay</span>
            </div>
            <a
              href="https://www.ebay.com/usr/northcyprusphilately"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition"
            >
              <span>View Our eBay Store</span>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </a>
          </div>
        </div>
      </div>

      <div className="bg-white py-16">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-4" data-testid="newsletter-title">{t.newsletter}</h2>
          <p className="text-lg mb-8 text-gray-600">{t.newsletterDesc}</p>
          {newsletterSuccess ? (
            <div className="flex items-center justify-center gap-2 text-green-600 text-lg">
              <CheckCircle size={24} />
              <span>Thank you for subscribing! Check your inbox for updates.</span>
            </div>
          ) : (
            <form onSubmit={handleNewsletterSubmit} className="flex gap-4 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Your email"
                className="flex-1"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                required
                data-testid="newsletter-email"
              />
              <button
                type="submit"
                className="btn-primary"
                disabled={newsletterSubmitting}
                data-testid="newsletter-subscribe"
              >
                {newsletterSubmitting ? '...' : t.subscribe}
              </button>
            </form>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default HomePage;